import { ActionIcon, Button, Checkbox, NumberInput, TextInput } from '@mantine/core';
import { IconPlus, IconRestore, IconSend, IconTrash } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import { MaterialPicker } from './MaterialPicker.js';
import { PasswordDialog } from './PasswordDialog.js';
import classes from './Purchasing.module.css';
import { formatQuantity, numberOf, numberSeparators, pickerClassOf } from './requisitions.js';
import type { PurchasingSource } from './source.js';
import type { RequisitionDetail, RequisitionRevisionInput } from './types.js';

/** Talep satırının ofis kararı (ekrandaki hali). */
interface ReviewRow {
  requisitionLineId: string;
  lineNo: number;
  name: string;
  unit: string;
  requested: number;
  onHand: number | null;
  approved: number | string;
  removed: boolean;
  note: string;
}

/** Ofisin eklediği katalog kalemi. */
interface AddedRow {
  key: string;
  materialId: string;
  name: string;
  unit: string;
  approved: number | string;
  note: string;
}

interface ReviewState {
  rows: ReviewRow[];
  added: AddedRow[];
}

/** Geçerli onaylı listeden başlangıç: son revizyon ya da talep olduğu gibi. */
export function reviewStateOf(detail: RequisitionDetail): ReviewState {
  const list = detail.approvedList ?? [];
  const onHand = new Map(detail.lines.map((line) => [line.id, line.onHand ?? null]));
  const rows: ReviewRow[] = list
    .filter((line) => line.requisitionLineId !== null)
    .map((line) => ({
      requisitionLineId: line.requisitionLineId as string,
      lineNo: line.lineNo ?? 0,
      name: line.name,
      unit: line.unit,
      requested: line.requested ?? 0,
      onHand: onHand.get(line.requisitionLineId as string) ?? null,
      // Kaldırılan satır geri alınırsa istenen miktarla döner
      approved: line.change === 'removed' ? (line.requested ?? 0) : line.approved,
      removed: line.change === 'removed',
      note: line.note ?? '',
    }));
  const added: AddedRow[] = list
    .filter((line) => line.change === 'added' && line.materialId !== null)
    .map((line) => ({
      key: line.key,
      materialId: line.materialId as string,
      name: line.name,
      unit: line.unit,
      approved: line.approved,
      note: line.note ?? '',
    }));
  return { rows, added };
}

/** Ekrandaki değişiklik sayısı: satır, eklenen ve çıkarılan eklenen. */
export function reviewChanges(initial: ReviewState, current: ReviewState): number {
  let count = 0;
  const before = new Map(initial.rows.map((row) => [row.requisitionLineId, row]));
  for (const row of current.rows) {
    const old = before.get(row.requisitionLineId);
    if (
      !old ||
      old.removed !== row.removed ||
      (!row.removed && numberOf(old.approved) !== numberOf(row.approved)) ||
      old.note.trim() !== row.note.trim()
    ) {
      count += 1;
    }
  }
  const was = new Map(initial.added.map((row) => [row.materialId, row]));
  const now = new Set(current.added.map((row) => row.materialId));
  for (const row of current.added) {
    const old = was.get(row.materialId);
    if (
      !old ||
      numberOf(old.approved) !== numberOf(row.approved) ||
      old.note.trim() !== row.note.trim()
    ) {
      count += 1;
    }
  }
  for (const row of initial.added) if (!now.has(row.materialId)) count += 1;
  return count;
}

/** Gönderilecek revizyon; miktarı geçersiz satır varsa `null`. */
export function revisionInputOf(
  state: ReviewState,
  reason: string,
): RequisitionRevisionInput | null {
  const lines: RequisitionRevisionInput['lines'] = [];
  for (const row of state.rows) {
    const note = row.note.trim() === '' ? null : row.note.trim();
    if (row.removed) {
      lines.push({ requisitionLineId: row.requisitionLineId, removed: true, note });
      continue;
    }
    const quantity = numberOf(row.approved);
    if (quantity === '' || quantity <= 0) return null;
    lines.push({ requisitionLineId: row.requisitionLineId, removed: false, quantity, note });
  }
  const added: RequisitionRevisionInput['added'] = [];
  for (const row of state.added) {
    const quantity = numberOf(row.approved);
    if (quantity === '' || quantity <= 0) return null;
    added.push({
      materialId: row.materialId,
      quantity,
      note: row.note.trim() === '' ? null : row.note.trim(),
    });
  }
  return { lines, added, reason: reason.trim() };
}

let addedCounter = 0;
/** Eklenen satırın ekrandaki anahtarı (React). */
function addedKey(): string {
  addedCounter += 1;
  return `added-${addedCounter}`;
}

/**
 * "Office Review" (ADR 0111 §A; sahip 2026-10-23: "ofis geminin verdiği talebi modifiye edip
 * gönderebilir"): onaylı talebin düzenlenebilir ızgarası. Requested ve In stock salt okunur;
 * Approved miktar, Remove ve Note düzenlenir; katalog kalemi eklenir. "Send to Vessel" gerekçe
 * ister ve değiştirilemez revizyon yazar; liste yalnız o gemiye iner.
 */
export function OfficeReviewPanel({
  detail,
  source,
  onSent,
}: {
  detail: RequisitionDetail;
  source: PurchasingSource;
  onSent: (next: RequisitionDetail) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.review.${key}`, options);
  const initial = useMemo(() => reviewStateOf(detail), [detail]);
  const [state, setState] = useState<ReviewState>(initial);
  const [picking, setPicking] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const changes = reviewChanges(initial, state);
  const q = (value: number) => formatQuantity(value, locale);
  const setRow = (id: string, patch: Partial<ReviewRow>) =>
    setState((current) => ({
      ...current,
      rows: current.rows.map((row) => (row.requisitionLineId === id ? { ...row, ...patch } : row)),
    }));
  const setAdded = (key: string, patch: Partial<AddedRow>) =>
    setState((current) => ({
      ...current,
      added: current.added.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    }));
  const listed = new Set([
    ...detail.lines.flatMap((line) => (line.materialId ? [line.materialId] : [])),
    ...state.added.map((row) => row.materialId),
  ]);
  const revise = source.revise;

  return (
    <>
      <p className={classes.notice} data-tone="info">
        {k('officeIntro')}
      </p>
      <section
        className={table.tableWrap}
        data-allow-scroll
        aria-label={k('grid')}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
        tabIndex={0}
      >
        <table className={`${table.table} ${classes.table}`}>
          <thead>
            <tr>
              <th scope="col" className={classes.index}>
                {k('columns.no')}
              </th>
              <th scope="col" className={classes.key}>
                {k('columns.item')}
              </th>
              <th scope="col" data-numeric>
                {k('columns.requested')}
              </th>
              <th scope="col" data-numeric>
                {k('columns.inStock')}
              </th>
              <th scope="col">{k('columns.approved')}</th>
              <th scope="col">{k('columns.uom')}</th>
              <th scope="col">{k('columns.remove')}</th>
              <th scope="col">{k('columns.note')}</th>
            </tr>
          </thead>
          <tbody>
            {state.rows.map((row) => {
              const quantity = numberOf(row.approved);
              const change = row.removed
                ? 'removed'
                : quantity !== '' && quantity !== row.requested
                  ? quantity > row.requested
                    ? 'increased'
                    : 'decreased'
                  : 'unchanged';
              return (
                <tr key={row.requisitionLineId} data-change={change}>
                  <td className={classes.index}>{row.lineNo}</td>
                  <td className={`${classes.material} ${classes.key}`}>{row.name}</td>
                  <td data-numeric>{q(row.requested)}</td>
                  <td data-numeric>{row.onHand === null ? '—' : q(row.onHand)}</td>
                  <td data-keep>
                    <NumberInput
                      className={classes.qty}
                      aria-label={k('approvedOf', { name: row.name })}
                      min={0}
                      decimalScale={3}
                      hideControls
                      disabled={row.removed}
                      {...numberSeparators(locale)}
                      value={row.approved}
                      onChange={(value) => setRow(row.requisitionLineId, { approved: value })}
                      error={
                        invalid && !row.removed && (quantity === '' || quantity <= 0)
                          ? true
                          : undefined
                      }
                    />
                  </td>
                  <td>{row.unit}</td>
                  <td data-keep>
                    <Checkbox
                      aria-label={k('removeOf', { name: row.name })}
                      checked={row.removed}
                      onChange={(event) =>
                        setRow(row.requisitionLineId, { removed: event.currentTarget.checked })
                      }
                    />
                  </td>
                  <td data-keep>
                    <TextInput
                      className={classes.lineDescription}
                      aria-label={k('noteOf', { name: row.name })}
                      maxLength={300}
                      value={row.note}
                      onChange={(event) =>
                        setRow(row.requisitionLineId, { note: event.currentTarget.value })
                      }
                    />
                  </td>
                </tr>
              );
            })}
            {state.added.map((row) => {
              const quantity = numberOf(row.approved);
              return (
                <tr key={row.key} data-change="added">
                  <td className={classes.index}>—</td>
                  <td className={`${classes.material} ${classes.key}`}>
                    {row.name}{' '}
                    <span className={classes.stockBadge}>
                      <StatusBadge tone="info">{k('added')}</StatusBadge>
                    </span>
                  </td>
                  <td data-numeric>—</td>
                  <td data-numeric>—</td>
                  <td>
                    <NumberInput
                      className={classes.qty}
                      aria-label={k('approvedOf', { name: row.name })}
                      min={0}
                      decimalScale={3}
                      hideControls
                      {...numberSeparators(locale)}
                      value={row.approved}
                      onChange={(value) => setAdded(row.key, { approved: value })}
                      error={invalid && (quantity === '' || quantity <= 0) ? true : undefined}
                    />
                  </td>
                  <td>{row.unit}</td>
                  <td>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      aria-label={k('removeAdded', { name: row.name })}
                      onClick={() =>
                        setState((current) => ({
                          ...current,
                          added: current.added.filter((item) => item.key !== row.key),
                        }))
                      }
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </td>
                  <td>
                    <TextInput
                      className={classes.lineDescription}
                      aria-label={k('noteOf', { name: row.name })}
                      maxLength={300}
                      value={row.note}
                      onChange={(event) => setAdded(row.key, { note: event.currentTarget.value })}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      <div className={classes.reviewFooter}>
        <Button
          variant="default"
          leftSection={<IconPlus size={16} />}
          onClick={() => setPicking(true)}
          style={{ justifySelf: 'start' }}
        >
          {k('addItem')}
        </Button>
        <div className={classes.reviewActions}>
          <span className={classes.muted} aria-live="polite">
            {changes > 0 ? k('changes', { count: changes }) : k('noChanges')}
          </span>
          <Button
            variant="default"
            leftSection={<IconRestore size={16} />}
            disabled={changes === 0}
            onClick={() => {
              setState(initial);
              setInvalid(false);
            }}
          >
            {k('reset')}
          </Button>
          <Button
            leftSection={<IconSend size={16} />}
            disabled={changes === 0 || !revise}
            onClick={() => {
              if (!revisionInputOf(state, 'x')) {
                setInvalid(true);
                return;
              }
              setInvalid(false);
              setConfirming(true);
            }}
          >
            {k('send')}
          </Button>
        </div>
      </div>
      <MaterialPicker
        opened={picking}
        source={source}
        existing={state.rows.length + state.added.length}
        catalogOnly
        materialClass={pickerClassOf(detail.department)}
        onClose={() => setPicking(false)}
        onAdd={(items) =>
          setState((current) => ({
            ...current,
            added: [
              ...current.added,
              ...items.flatMap((item) =>
                item.materialId === null || listed.has(item.materialId)
                  ? []
                  : [
                      {
                        key: addedKey(),
                        materialId: item.materialId,
                        name: item.name,
                        unit: item.unit,
                        approved: item.quantity,
                        note: '',
                      },
                    ],
              ),
            ],
          }))
        }
      />
      <PasswordDialog
        opened={confirming}
        title={k('confirmTitle')}
        body={k('confirmBody')}
        reason="required"
        reasonLabel={k('reason')}
        password
        reauthKind="requisition_revision"
        confirmLabel={k('send')}
        onClose={() => setConfirming(false)}
        onConfirm={async ({ reason, password }) => {
          const input = revisionInputOf(state, reason);
          if (!input || !revise) return 'VALIDATION_FAILED';
          try {
            const next = await revise(detail.id, {
              ...input,
              ...(password ? { password } : {}),
            });
            setConfirming(false);
            onSent(next);
            return null;
          } catch (failure) {
            return errorCode(failure);
          }
        }}
      />
    </>
  );
}
